import { useCallback } from 'react'
import { useRecoilCallback } from 'recoil'
import type { ApplicationType } from '@messenger/types'
import {
  createEditorSaveVersion,
  type EditorSaveVersionReason,
} from '@/api/orderProducts'
import { editorSaveVersionSupportedAtom } from '@/atoms/_editorAtoms'
import { editorProductIdAtom } from '@/atoms/editorAtoms'
import {
  disablePreSaveVersionForSession,
  preSaveVersionDisabledForSession,
} from '@/utils/editor/editorSaveSession'
import { logger, EventCategory } from '@/utils/logger'
import { useRecoilLogData } from '@/utils/recoilLogger'

/** Why a Save is recording a version per page write instead of one. */
export type EditorSaveVersionFallbackCause =
  | 'unsupported'
  | 'no_line'
  | 'skipped_for_session'
  | 'no_version'
  | 'failed_fast'
  | 'failed_slow'
  | 'timed_out'

/** The version covering a Save: its number, or why there is none. */
export type EditorSaveVersionAnswer =
  | { versionNumber: number; fallbackCause?: undefined }
  | {
      versionNumber: undefined
      fallbackCause: EditorSaveVersionFallbackCause
    }

/**
 * A failure inside the server's own wait for the line lock cannot be that
 * wait: nothing had queued yet, so the call was refused or never left the
 * network, and the next Save may as well ask again. Past it, the server is
 * answering slowly, and every later Save would pay the same wait before
 * falling back anyway.
 */
const SLOW_FAILURE_AFTER_MS = 5_000

const isTimeoutError = (error: unknown): boolean => {
  const code = (error as { code?: string } | null)?.code
  return code === 'ECONNABORTED' || code === 'ETIMEDOUT'
}

/**
 * The version covering this Save, asked for before the Save writes anything,
 * under the gesture's own reason when it names one.
 *
 * Answers the number the page writes should name, or no number and the
 * reason there is none — a Save is never failed for versioning's sake, and a
 * Save with no number simply records a version per page write. Every one of
 * those falls back for a reason, and logs it.
 */
export const useEditorSaveVersion = () => {
  const recoilLogData = useRecoilLogData()

  const readSaveVersionContext = useRecoilCallback(
    ({ snapshot }) =>
      () => ({
        supported:
          snapshot.getLoadable(editorSaveVersionSupportedAtom).valueMaybe() ??
          false,
        orderProductId: snapshot
          .getLoadable(editorProductIdAtom)
          .valueMaybe() as string | undefined,
      }),
    [],
  )

  return useCallback(
    async (
      applicationType: ApplicationType,
      reason?: EditorSaveVersionReason,
    ): Promise<EditorSaveVersionAnswer> => {
      // A route this release does not have, a line not loaded yet, a tab that
      // already gave up, a line the server found nothing to version: expected
      // answers, and the Save goes on. A call that failed or expired is the
      // route not working, which is what an operator needs to see.
      const fallBack = (
        cause: EditorSaveVersionFallbackCause,
        data: Record<string, unknown> = {},
      ): EditorSaveVersionAnswer => {
        const entry = {
          message: 'editor_save_version_fallback',
          category: EventCategory.SAVE,
          data: {
            ...recoilLogData,
            cause,
            ...(reason !== undefined && { reason }),
            ...data,
          },
        }
        if (
          cause === 'failed_fast' ||
          cause === 'failed_slow' ||
          cause === 'timed_out'
        )
          logger.error({
            ...entry,
            category: EventCategory.ERROR,
            error: new Error(`editor save version ${cause}`),
          })
        else logger.info(entry)
        return { versionNumber: undefined, fallbackCause: cause }
      }

      const { supported, orderProductId } = readSaveVersionContext()
      if (!supported) return fallBack('unsupported')
      if (!orderProductId) return fallBack('no_line')
      if (preSaveVersionDisabledForSession())
        return fallBack('skipped_for_session')

      const startedAt = Date.now()
      try {
        const { data } = await createEditorSaveVersion(
          orderProductId,
          applicationType,
          reason,
        )
        const versionNumber = data?.version_number
        // The route answers a number even when it wrote nothing, because the
        // line already equalled a version; no number means it could not take
        // one at all.
        if (typeof versionNumber !== 'number') return fallBack('no_version')
        return { versionNumber }
      } catch (error) {
        const elapsedMs = Date.now() - startedAt
        const timedOut = isTimeoutError(error)
        const slow = timedOut || elapsedMs >= SLOW_FAILURE_AFTER_MS
        if (slow) disablePreSaveVersionForSession()
        return fallBack(
          timedOut ? 'timed_out' : slow ? 'failed_slow' : 'failed_fast',
          {
            elapsedMs,
            error: error instanceof Error ? error.message : String(error),
          },
        )
      }
    },
    [readSaveVersionContext, recoilLogData],
  )
}

export default useEditorSaveVersion
